var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import z from 'zod';
/**
 * A composite hook for making API requests and handling the response.
 *
 * @param apiFunc The API function to call
 * @param responseSchema The schema to validate the response data against. While this parameter is optional, it is highly recommended to
 *                       provide type safety the response data.
 * @param clearDataOnRequest If true, the data will be cleared when a new request is made (i.e. why the new request is pending and new data
 *                           is not yet available). Default is true.
 * @param debounceDelay If set to a non-zero value, the request will not be sent until the delay elapses, and any subsequent invocation of
 *                      the request callback will replace the previous invocation and reset the delay.
 */
function useApi(apiFunc, responseSchema, clearDataOnRequest = true, debounceDelay = 0) {
    const [response, setResponse] = useState(null);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    // eslint-disable-next-line no-undef
    const [debounceCallbackId, setDebounceCallbackId] = useState(null);
    const [queuedArgs, setQueuedArgs] = useState(null);
    const internalRequest = (...args) => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        // clear out previous api results
        if (clearDataOnRequest) {
            setData(null);
        }
        setError(null);
        try {
            const result = yield apiFunc(...args);
            setResponse(result);
            if (result.data) {
                try {
                    const parsedData = (responseSchema !== null && responseSchema !== void 0 ? responseSchema : z.any()).parse(result.data);
                    setData(parsedData);
                }
                catch (e) {
                    console.warn(`Failed to parse response data for ${apiFunc.name || 'anonymous'} as ${(responseSchema === null || responseSchema === void 0 ? void 0 : responseSchema.description) || 'unknown'}`, e);
                    // Even if schema validation fails, try to continue anyway using the raw data as is
                    setData(result.data);
                }
            }
            else {
                setData(null);
            }
        }
        catch (err) {
            setError(err || 'Unknown Error');
        }
        finally {
            setLoading(false);
            setLoaded(true);
        }
    });
    const request = (...args) => __awaiter(this, void 0, void 0, function* () {
        if (debounceDelay) {
            if (debounceCallbackId) {
                clearTimeout(debounceCallbackId);
            }
            setDebounceCallbackId(setTimeout(() => {
                setQueuedArgs(args);
            }, debounceDelay));
            setLoading(true);
        }
        else {
            yield internalRequest(...args);
        }
    });
    useEffect(() => {
        if (queuedArgs) {
            void internalRequest(...queuedArgs);
            setQueuedArgs(null);
        }
    }, [queuedArgs]);
    return {
        response,
        data,
        error,
        loading,
        loaded,
        request,
    };
}
export default useApi;
//# sourceMappingURL=useApi.js.map